import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Award, Bell, Calendar, ChevronRight, CreditCard, FileText, Hourglass, MailWarning, PlusCircle, Send, Trophy, Users, Wallet,
} from "lucide-react";

import hero from "@/assets/hero.jpg";
import { api } from "@/lib/api";
import { DossierItem } from "@/components/dossier-item";
import { MonthlyChart } from "@/components/monthly-chart";
import { Brand } from "@/components/brand";
import { Button, Empty, ErrorView, IconButton, Loading, Stat } from "@/components/kit";
import { useToast } from "@/components/toast";
import { C, eur } from "@/lib/format";

function EarningsHero({ d }) {
  return (
    <section className="relative overflow-hidden rounded-[20px] fade-up">
      <img src={hero} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/55 to-[#121212]/90" />
      <div className="relative flex flex-col gap-2 p-6 md:p-8">
        <p className="t-label !text-[#F3E5AB]">TOTAL COMMISSIONS GAGNÉES</p>
        <p data-testid="home-earned-total" className="t-display tabular-nums !text-[#D4AF37] md:!text-[44px] md:!leading-[52px]">{eur(d.commissions.earned)}</p>
        <div className="mt-2 flex gap-4">
          <div className="flex-1 rounded-xl bg-[#1E1E1E]/80 p-3 backdrop-blur-md">
            <p className="t-caption">En attente de validation</p>
            <p data-testid="home-pending-total" className="t-h3 !text-[#F2994A]">{eur(d.commissions.pending)}</p>
          </div>
          <div className="flex-1 rounded-xl bg-[#1E1E1E]/80 p-3 backdrop-blur-md">
            <p className="t-caption">Déjà payé</p>
            <p data-testid="home-paid-total" className="t-h3 !text-[#27AE60]">{eur(d.commissions.paid)}</p>
          </div>
        </div>
        <Link to="/commissions" data-testid="home-commissions-link" className="focus-ring mt-1 inline-flex w-fit items-center gap-1 rounded text-sm font-bold text-[#D4AF37] hover:underline">
          Détail de mes commissions <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

function Banner({ color, icon: Icon, children, testID, to }) {
  const cls = "flex items-center gap-3 rounded-[20px] border bg-[#292415] p-4";
  if (to)
    return (
      <Link to={to} data-testid={testID} className={`${cls} pressable`} style={{ borderColor: color }}>
        <Icon className="h-5 w-5 shrink-0" style={{ color }} aria-hidden />
        <div className="flex-1 text-sm text-white">{children}</div>
        <ChevronRight className="h-5 w-5 text-[#9E9E9E]" aria-hidden />
      </Link>
    );
  return (
    <div data-testid={testID} className={cls} style={{ borderColor: color }}>
      <Icon className="h-5 w-5 shrink-0" style={{ color }} aria-hidden />
      <div className="flex-1">{children}</div>
    </div>
  );
}

function RankingCard({ r }) {
  return (
    <Link to="/ranking" data-testid="home-ranking-card" className="pressable flex items-center gap-3 rounded-[20px] border border-[#333333] bg-[#1E1E1E] p-4">
      <Trophy className="h-7 w-7 text-[#D4AF37]" aria-hidden />
      <div className="flex-1">
        <h3 className="t-h3">Classement national</h3>
        <p className="t-caption">
          {r.year ? `${r.year}e cette année · ${r.year_points} pts` : "Pas encore classé cette année"}
          {r.month ? ` · ${r.month}e ce mois-ci` : ""}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 text-[#9E9E9E]" aria-hidden />
    </Link>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const toast = useToast();
  const q = useQuery({ queryKey: ["agent-dashboard"], queryFn: () => api("/agent/dashboard") });
  const [sending, setSending] = useState(false);

  const resend = async () => {
    setSending(true);
    try {
      await api("/auth/resend-verification", { method: "POST" });
      toast("E-mail de confirmation renvoyé.");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSending(false);
    }
  };

  const d = q.data;
  return (
    <div data-testid="agent-home-screen" className="flex min-h-full flex-col">
      <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-[#222222] bg-[#121212]/90 px-4 py-3 backdrop-blur-md md:px-8">
        <div className="flex-1 lg:hidden"><Brand compact /></div>
        <p className="t-caption hidden flex-1 tracking-widest lg:block">ESPACE PARTENAIRE</p>
        <IconButton icon={Bell} label="Notifications" testID="home-notifications-button" badge={d?.unread_notifications} onClick={() => navigate("/notifications")} />
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.isError || !d ? (
        <ErrorView message="Impossible de charger votre tableau de bord." onRetry={() => q.refetch()} />
      ) : (
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4 pb-32 md:p-8 lg:pb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="t-h1 md:!text-[32px] md:!leading-[38px]">Bonjour {d.user.first_name}</h1>
            <div className="fixed inset-x-4 bottom-20 z-20 lg:static lg:inset-auto lg:z-auto">
              <Button testID="recommend-client-cta" title="RECOMMANDER UN CLIENT" icon={PlusCircle} size="lg" className="w-full lg:w-auto lg:px-8" onClick={() => navigate("/recommend")} />
            </div>
          </div>

          {!d.user.email_verified && (
            <Banner color={C.warning} icon={MailWarning} testID="email-verify-banner">
              <p className="text-sm text-white">Confirmez votre adresse e-mail pour sécuriser votre compte.</p>
              <button type="button" data-testid="resend-verification-button" onClick={resend} disabled={sending} className="focus-ring mt-1 rounded text-sm font-bold text-[#D4AF37] hover:underline">
                {sending ? "Envoi…" : "Renvoyer l'e-mail"}
              </button>
            </Banner>
          )}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
            <EarningsHero d={d} />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Stat testID="stat-total" icon={Send} label="Recommandations envoyées" value={d.stats.total} />
              <Stat testID="stat-active" icon={Hourglass} label="Prospects en cours" value={d.stats.active} />
              <Stat testID="stat-rdv" icon={Calendar} label="Rendez-vous réalisés" value={d.stats.rdv} />
              <Stat testID="stat-devis" icon={FileText} label="Devis transmis" value={d.stats.devis} />
              <Stat testID="stat-signed" icon={Award} label="Contrats signés" value={d.stats.signed} accent={C.success} />
              <Stat testID="stat-remaining" icon={Wallet} label="Reste à percevoir" value={eur(d.commissions.remaining)} />
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,4fr)]">
            <MonthlyChart path="/agent/stats/monthly" testID="agent-monthly-chart" />
            <div className="flex flex-col gap-4">
              <RankingCard r={d.ranking} />
              {!d.user.has_rib && (
                <Banner to="/rib" color={C.brand} icon={CreditCard} testID="home-rib-missing-banner">
                  RIB manquant : ajoutez vos coordonnées bancaires pour recevoir vos paiements.
                </Banner>
              )}
            </div>
          </div>

          <div className="mt-2 flex items-center justify-between">
            <h2 className="t-h2 font-display !font-normal">Mes derniers dossiers</h2>
            {d.recent.length > 0 && (
              <Link to="/dossiers" data-testid="home-see-all-dossiers" className="focus-ring rounded text-sm text-[#D4AF37] hover:underline">
                Tout voir
              </Link>
            )}
          </div>
          {d.recent.length === 0 ? (
            <Empty icon={Users} title="Aucune recommandation pour l'instant" text="Appuyez sur « Recommander un client » pour envoyer votre premier dossier et commencer à gagner des commissions." />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {d.recent.map((r) => <DossierItem key={r.id} d={r} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
